import React, { useEffect } from 'react';
import { AlertTriangle, Info, Loader2, Paperclip, Send, Shield, User } from 'lucide-react';
import { CATEGORIAS, CATEGORIAS_IDENTIFICACAO_OBRIGATORIA } from '../constants.js';

export const PublicForm = ({ states, handlers }) => {
  const { categoria, descricao, anonimo, nome, email, telefone, anexos, isSubmitting } = states;
  const { setCategoria, setDescricao, setAnonimo, setNome, setEmail, setTelefone, handleFileChange, removeAnexo, handleSubmit } = handlers;
  const isIdentificacaoObrigatoria = CATEGORIAS_IDENTIFICACAO_OBRIGATORIA.includes(categoria);

  useEffect(() => {
    if (isIdentificacaoObrigatoria) {
      setAnonimo(false);
    }
  }, [categoria, isIdentificacaoObrigatoria, setAnonimo]);

  return (
    // Alterado para max-w-5xl para alargar o formulário e p-5/mt-4 para reduzir espaços vazios
    <div className="max-w-5xl mx-auto mt-4 p-5 md:p-6 bg-white rounded-xl shadow-lg border border-slate-100 animate-fadeIn">
      
      {/* Cabeçalho mais compacto */}
      <div className="mb-5 text-center">
        <Shield className="mx-auto text-blue-600 mb-2" size={36} />
        <h2 className="text-2xl font-bold text-slate-800">Novo Relato</h2>
        <p className="text-slate-600 mt-1 text-xs sm:text-sm">Este é um ambiente seguro. A RTA garante a confidencialidade das informações e a não retaliação contra quem relata de boa-fé.</p>
      </div>

      {/* Informativo Fixo Centralizado */}
      <div className="mb-6 bg-[#f4f8ff] rounded-r-lg border-l-4 border-blue-500 p-4 flex items-center justify-center gap-3 shadow-sm">
        <Info size={20} className="text-blue-600 shrink-0" />
        <p className="text-sm text-blue-900 font-medium leading-relaxed text-center">
          Prezado cliente, caso queira saber como será o processo de tratamento da sua reclamação, solicite à RTA que envie cópia do PA 05 - Procedimento de tratamento de reclamação.
        </p>
      </div>

      {/* Formulário Dividido em 2 Colunas no Desktop (md:grid-cols-2) */}
      <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
        
        {/* === COLUNA DA ESQUERDA === */}
        <div className="flex flex-col space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Qual a natureza da situação? *</label>
            <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className="w-full p-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-slate-50 outline-none text-sm" required>
              <option value="" disabled>Selecione uma opção...</option>
              {CATEGORIAS.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div className="flex-1 flex flex-col">
            <label className="block text-sm font-semibold text-slate-700 mb-1">Descreva o que aconteceu em detalhes *</label>
            <p className="text-[11px] text-slate-500 mb-2">Inclua datas, locais e nomes de pessoas envolvidas.</p>
            {/* O textarea agora estica automaticamente e tem menos linhas por padrão */}
            <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} rows="4" className="w-full flex-1 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-slate-50 outline-none resize-none text-sm" placeholder="Descreva a situação aqui..." required />
          </div>
        </div>

        {/* === COLUNA DA DIREITA === */}
        <div className="flex flex-col space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Anexar Evidências (Opcional)</label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-3 bg-slate-50 text-center">
              <input type="file" multiple onChange={handleFileChange} className="hidden" id="file-upload" />
              <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center gap-1 text-slate-600 hover:text-blue-600 transition-colors">
                <Paperclip size={20} />
                <span className="text-xs font-medium">Clique para anexar arquivos (Fotos, Áudios, PDFs)</span>
              </label>
            </div>
            {anexos.length > 0 && (
              <ul className="mt-2 space-y-1 max-h-24 overflow-y-auto">
                {anexos.map((file, index) => (
                  <li key={index} className="flex items-center justify-between bg-white p-1.5 border border-slate-200 rounded text-xs text-slate-700">
                    <span className="truncate flex-1 pr-2">{file.name}</span>
                    <button type="button" onClick={() => removeAnexo(index)} className="text-red-500 hover:text-red-700 font-bold">X</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3"><User size={16} /> Identificação</h3>
            {isIdentificacaoObrigatoria ? (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-2.5 rounded-md flex gap-2 text-xs mb-3">
                <AlertTriangle size={16} className="shrink-0" />
                <p>Identificação obrigatória para <strong>{categoria}</strong>.</p>
              </div>
            ) : (
              <label className="flex items-center gap-2 mb-3 cursor-pointer">
                <input type="checkbox" checked={anonimo} onChange={(e) => setAnonimo(e.target.checked)} className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500" />
                <span className="text-xs font-bold uppercase text-slate-700">Desejo fazer este relato de forma anônima.</span>
              </label>
            )}
            
            {/* Campos de contato mais enxutos */}
            {!anonimo && (
              <div className="space-y-3 animate-fadeIn">
                <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} className="w-full p-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Nome Completo *" required={!anonimo} />
                <div className="grid grid-cols-2 gap-3">
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" placeholder="E-mail" />
                  <input type="tel" value={telefone} onChange={(e) => setTelefone(e.target.value)} className="w-full p-2 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Telefone / WhatsApp" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* === BOTÃO DE ENVIAR OCUPANDO A LARGURA TOTAL === */}
        <div className="md:col-span-2 mt-2">
          <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-md disabled:bg-slate-400 disabled:cursor-not-allowed">
            {isSubmitting ? <><Loader2 size={18} className="animate-spin" /> Enviando...</> : <><Send size={18} /> Enviar Relato de Forma Segura</>}
          </button>
        </div>
      </form>
    </div>
  );
};
